/**
 * Schatten-Konvergenz: GmbH-GF-Lohn vs. Lohnfaktor (Zirkelbezug).
 * Pure function — keine Store-Writes, kein Write-Back in personalData.
 * Lohnnebenkosten (AG) ausschließlich dynamisch über lohnnebenkostenProzent.
 */

export interface GmbhGfConvergenceParams {
  gfZielgehalt: number;
  gfUmsatz: number;
  currentLohnfaktor: number;
  /** Sollumsatz-Basis OHNE den GF (aktueller Selektor-Stand) */
  sollumsatz: number;
  produktiveLohnkostenMitarbeiterOhneAzubis: number;
  lohnfortzahlungPercent: number;
  mwstSatz: number;
  sicherheitsfaktor: number;
  /** Dynamischer AG-LNK-Satz aus globalen Country-/Store-Daten (z. B. 21.5) — niemals hardcodieren */
  lohnnebenkostenProzent: number;
}

export interface GmbhGfConvergenceResult {
  convergedLohnfaktor: number;
  produktiverLohn: number;
  unproduktiverLohn: number;
}

const LOHNFAKTOR_AKTIV_MIN = 3.5;
const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Simulierter lohnfaktorAktiv nach virtuellem Einbuchen von P (prod) und U (unprod)
 * inkl. dynamischer AG-Lohnnebenkosten.
 *
 * ΔProduktiverLohn = P
 * ΔGemeinkosten   = U·(1+lnk) + P·lnk
 * → ΔSollumsatz   = (P+U)·(1+lnk)
 */
function simulateLohnfaktorAktiv(
  P: number,
  U: number,
  p: GmbhGfConvergenceParams,
  lnkFaktor: number
): number {
  const unprodKostenInklLnk = U * (1 + lnkFaktor);
  const prodLnk = P * lnkFaktor;
  const deltaGemeinkosten = unprodKostenInklLnk + prodLnk;
  const deltaProduktiverLohn = P;

  const ohneAzubis =
    (Number(p.produktiveLohnkostenMitarbeiterOhneAzubis) || 0) + deltaProduktiverLohn;
  const sollumsatz =
    (Number(p.sollumsatz) || 0) + deltaGemeinkosten + deltaProduktiverLohn;

  const lohnfortzahlung = ohneAzubis * ((Number(p.lohnfortzahlungPercent) || 0) / 100);
  const produktiveKostenTabelle = ohneAzubis;
  if (!(produktiveKostenTabelle > 0)) {
    return Math.max(LOHNFAKTOR_AKTIV_MIN, Number(p.currentLohnfaktor) || LOHNFAKTOR_AKTIV_MIN);
  }

  const kalkSollumsatzInklFortzahlung = sollumsatz + lohnfortzahlung;
  const lohnfaktorNetto = kalkSollumsatzInklFortzahlung / produktiveKostenTabelle;
  const lohnfaktorBrutto = lohnfaktorNetto * (1 + (Number(p.mwstSatz) || 0) / 100);
  const lohnfaktorFortzahlung =
    lohnfaktorBrutto * (1 + (Number(p.sicherheitsfaktor) || 0) / 100);

  return Math.max(LOHNFAKTOR_AKTIV_MIN, lohnfaktorFortzahlung);
}

/**
 * Fixed-Point: LF inkl. GF ↔ produktiver/unproduktiver GF-Lohn.
 * Max. 15 Schritte, Abbruch |ΔL| < 0.005.
 */
export function calculateGmbhGfConvergence(
  params: GmbhGfConvergenceParams
): GmbhGfConvergenceResult {
  const gfZiel = Math.max(0, Number(params.gfZielgehalt) || 0);
  const gfUmsatz = Math.max(0, Number(params.gfUmsatz) || 0);
  const lnkFaktor = (Number(params.lohnnebenkostenProzent) || 0) / 100;

  let L =
    Number(params.currentLohnfaktor) > 0
      ? Number(params.currentLohnfaktor)
      : LOHNFAKTOR_AKTIV_MIN;

  let P = 0;
  let U = Math.max(0, gfZiel);

  if (!(gfUmsatz > 0) || !(L > 0)) {
    return {
      convergedLohnfaktor: round2(L),
      produktiverLohn: 0,
      unproduktiverLohn: Math.round(U),
    };
  }

  for (let i = 0; i < 15; i += 1) {
    P = Math.round(gfUmsatz / L);
    U = Math.max(0, gfZiel - P);
    const Lnext = simulateLohnfaktorAktiv(P, U, params, lnkFaktor);
    if (!Number.isFinite(Lnext) || Lnext <= 0) break;
    if (Math.abs(Lnext - L) < 0.005) {
      L = Lnext;
      break;
    }
    L = Lnext;
  }

  // Final split mit konvergiertem L
  P = Math.round(gfUmsatz / L);
  U = Math.max(0, gfZiel - P);

  return {
    convergedLohnfaktor: round2(L),
    produktiverLohn: P,
    unproduktiverLohn: Math.round(U),
  };
}
